import React from 'react';
import { Building2, CalendarDays, DollarSign, Clock, Users, MapPin } from 'lucide-react';

function fmtLocal(d) {
  try { return new Date(d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }); }
  catch { return d; }
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-2">
    <Icon size={14} className="flex-shrink-0 text-muted-foreground" />
    <span className="w-28 flex-shrink-0 text-xs text-muted-foreground">{label}</span>
    <span className="text-sm font-medium text-card-foreground">{value || '—'}</span>
  </div>
);

const Step3Review = ({ selectedCustomer, schedule }) => {
  const { serviceType, hourlyPayRate, formattedAddress, shifts } = schedule;

  const validShifts = shifts.filter((s) => s.date && s.startTime && s.endTime);

  return (
    <div className="space-y-5">
      <p className="text-sm text-muted-foreground">
        Review all details before creating the job.
      </p>

      {/* Customer */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Customer</p>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
            <Building2 size={18} className="text-primary" />
          </div>
          <div>
            <p className="text-sm font-bold text-card-foreground">
              {selectedCustomer?.companyFriendlyName || selectedCustomer?.companyLegalName || '—'}
            </p>
            {selectedCustomer?.companyLegalName && selectedCustomer?.companyFriendlyName && (
              <p className="text-xs text-muted-foreground">{selectedCustomer.companyLegalName}</p>
            )}
          </div>
        </div>
      </div>

      {/* Job details */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Job Details</p>
        <div className="divide-y divide-border">
          <Row icon={CalendarDays} label="Service Type"  value={serviceType} />
          <Row icon={DollarSign}   label="Hourly Rate"   value={hourlyPayRate ? `$${Number(hourlyPayRate).toFixed(2)}/hr` : null} />
          <Row icon={MapPin}       label="Location"      value={formattedAddress} />
        </div>
      </div>

      {/* Shifts */}
      <div className="rounded-xl border border-border bg-card p-4 shadow-sm">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Shifts ({validShifts.length})
          </p>
          {validShifts.length !== shifts.length && (
            <p className="text-xs text-amber-600">
              {shifts.length - validShifts.length} shift{shifts.length - validShifts.length !== 1 ? 's' : ''} incomplete
            </p>
          )}
        </div>
        {validShifts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No valid shifts configured.</p>
        ) : (
          <div className="overflow-auto rounded-lg border border-border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">#</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Date</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">Time</th>
                  <th className="px-3 py-2 text-left font-semibold text-muted-foreground">
                    <div className="flex items-center gap-1"><Users size={10} /> Staff</div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {validShifts.map((s, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 text-muted-foreground">{i + 1}</td>
                    <td className="px-3 py-2 font-medium">{fmtLocal(s.date)}</td>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-1">
                        <Clock size={10} className="text-muted-foreground" />
                        {s.startTime} – {s.endTime}
                      </div>
                    </td>
                    <td className="px-3 py-2">{s.staffsRequired}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Step3Review;
